<template>
    <main class="cb-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">
                <router-link to="/admin/companies" class="tbv2-crumb">{{ $t('admin.companies.title') }}</router-link>
                <span class="tbv2-sep">/</span>
                <span class="tbv2-current">{{ title }}</span>
            </div>
        </portal>

        <header class="cb-hero">
            <router-link :to="backLink" class="cb-back">
                <i class="fal fa-arrow-left"></i>
                <span>{{ $t('common.back') || 'Retour' }}</span>
            </router-link>
            <h1 class="cb-hero-title">{{ title }}</h1>
            <p class="cb-hero-sub">{{ subtitle }}</p>
        </header>

        <section class="cb-card">
            <header class="cb-card-header">
                <div class="cb-card-icon">
                    <i class="fal fa-building"></i>
                </div>
                <div>
                    <h2 class="cb-card-title">{{ title }}</h2>
                    <p class="cb-card-desc">{{ subtitle }}</p>
                </div>
            </header>

            <form class="cb-form" @submit.prevent="doCreate">
                <div class="cb-grid">
                    <div class="cb-field">
                        <label class="cb-label" for="company-name">{{ $t('companies.company_name') }}</label>
                        <input
                            id="company-name"
                            v-model="company.name"
                            type="text"
                            class="cb-input"
                        />
                    </div>

                    <div class="cb-field">
                        <label class="cb-label" for="company-slug">{{ $t('companies.slug') }}</label>
                        <input
                            id="company-slug"
                            v-model="company.slug"
                            type="text"
                            class="cb-input cb-input--mono"
                        />
                    </div>
                </div>

                <div class="cb-section-divider">
                    <span class="cb-section-label">Logo</span>
                </div>

                <div v-if="isEdit && currentLogoSrc && !logoFile" class="cb-current-logo">
                    <span class="cb-current-logo-label">{{ $t('companies.current_logo') || 'Logo actuel' }}</span>
                    <div class="cb-current-logo-frame">
                        <img :src="currentLogoSrc" alt="Logo actuel" class="cb-current-logo-img" />
                    </div>
                </div>

                <div class="cb-field cb-field--upload">
                    <BmvFileUpload
                        :accepted-extensions="['png', 'jpg', 'jpeg']"
                        @input="logoFile = $event"
                    />
                </div>

                <div v-if="!saved" class="cb-actions">
                    <router-link :to="backLink" class="cb-btn cb-btn--ghost">
                        {{ $t('common.cancel') || 'Annuler' }}
                    </router-link>
                    <button
                        type="submit"
                        class="cb-btn cb-btn--primary"
                        :disabled="loading"
                    >
                        <i v-if="loading" class="fas fa-circle-notch fa-spin"></i>
                        <i v-else class="fal fa-check"></i>
                        {{ submitLabel }}
                    </button>
                </div>
            </form>
        </section>
    </main>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import BmvFileUpload from '@/components/UI/BmvFileUpload'

export default {
    name: 'CompanyBuilder',
    components: { BmvFileUpload },
    data() {
        return {
            loading: false,
            saved: false,
            logoFile: null
        }
    },
    computed: {
        ...mapState('admin', {
            company: (state) => state.newCompany,
            companySettings: (state) => state.companySettings
        }),
        ...mapState('auth', { token: (state) => state.token }),
        currentLogoSrc() {
            if (!this.companySettings || !this.companySettings.settings_logo) return null
            const logo = this.companySettings.settings_logo.find((l) => l.is_logo)
            if (!logo) return null
            const companyId = this.$route.params.companyId
            return `${process.env.VUE_APP_BACKEND_URL}/api/${companyId}/storage/logo/${logo.id}?token=${this.token}`
        },
        isEdit() {
            return !!this.$route.params.companyId
        },
        title() {
            return this.isEdit
                ? this.$t('companies.edit')
                : this.$t('companies.create')
        },
        subtitle() {
            return this.isEdit
                ? this.$t('companies.edit_subtitle')
                : this.$t('companies.create_subtitle')
        },
        submitLabel() {
            return this.isEdit
                ? this.$t('companies.edit_company')
                : this.$t('companies.create_company')
        },
        backLink() {
            const id = this.$route.params.companyId
            return id ? `/admin/companies/${id}` : '/admin/companies'
        }
    },
    async created() {
        const companyId = this.$route.params.companyId
        if (!companyId) return
        this.loading = true
        try {
            const response = await this.getCompany(companyId)
            const loaded = response.data.company
            if (loaded) {
                this.company.name = loaded.name || ''
                this.company.slug = loaded.slug || ''
            }
        } catch (e) {
            this.$store.dispatch('dispatchError', e)
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapActions('admin', ['createCompany', 'updateCompany', 'getCompany']),
        async doCreate() {
            this.loading = true
            const companyId = this.$route.params.companyId
            const payload = {
                name: this.company.name,
                slug: this.company.slug
            }
            if (this.logoFile) {
                payload.settings_logo = {
                    name: this.logoFile.name,
                    encodedFile: this.logoFile.encodedFile
                }
            }
            if (companyId) payload.id = companyId
            const request = companyId ? this.updateCompany(payload) : this.createCompany(payload)
            request
                .then(() => {
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('success.title'),
                        message: this.$t('success.message')
                    })
                    this.$router.push(this.backLink)
                })
                .catch((e) => {
                    this.$store.dispatch('dispatchError', e)
                })
                .finally(() => {
                    this.loading = false
                })
        }
    }
}
</script>

<style scoped>
.cb-v2 {
    width: 100%;
    max-width: 780px;
    margin: 0 auto;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.tbv2-crumb {
    color: var(--v2-text-3);
    text-decoration: none !important;
    font-weight: var(--v2-font-weight-medium);
    transition: color var(--v2-transition-fast);
}

.tbv2-crumb:hover {
    color: var(--v2-primary);
}

.tbv2-sep { color: var(--v2-text-4); }
.tbv2-current { color: var(--v2-text-1); }

.cb-hero {
    margin-bottom: var(--v2-space-5);
}

.cb-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    text-decoration: none !important;
    margin-bottom: 10px;
    transition: color var(--v2-transition-fast);
}

.cb-back:hover {
    color: var(--v2-primary);
}

.cb-back i {
    font-size: 0.75rem;
}

.cb-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.cb-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
    max-width: 640px;
}

.cb-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.cb-card-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 22px;
    border-bottom: 1px solid var(--v2-border);
}

.cb-card-icon {
    width: 40px;
    height: 40px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1.05rem;
}

.cb-card-title {
    margin: 0;
    font-size: 1rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
}

.cb-card-desc {
    margin: 2px 0 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
}

.cb-form {
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.cb-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.cb-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.cb-field--upload {
    gap: 10px;
}

.cb-label {
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-2);
    letter-spacing: 0.01em;
    margin: 0;
}

.cb-input {
    width: 100%;
    box-sizing: border-box;
    height: 38px;
    padding: 0 12px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-1);
    font-family: inherit;
    outline: none;
    transition: border-color var(--v2-transition-fast), box-shadow var(--v2-transition-fast);
}

.cb-input:hover {
    border-color: var(--v2-border-strong);
}

.cb-input:focus {
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12);
}

.cb-input--mono {
    font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
    font-size: 0.82rem;
}

.cb-section-divider {
    display: flex;
    align-items: center;
    padding-top: 6px;
    margin-top: 2px;
    border-top: 1px solid var(--v2-border-subtle);
}

.cb-section-label {
    font-size: 0.68rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-3);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding-top: 14px;
}

.cb-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 14px;
    border-top: 1px solid var(--v2-border);
    margin-top: 4px;
}

.cb-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 36px;
    padding: 0 16px;
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    border: 1px solid transparent;
    cursor: pointer;
    text-decoration: none !important;
    transition: all var(--v2-transition-fast);
    font-family: inherit;
}

.cb-btn i {
    font-size: 0.8rem;
}

.cb-btn--ghost {
    background: transparent;
    border-color: var(--v2-border);
    color: var(--v2-text-2);
}

.cb-btn--ghost:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.cb-btn--primary {
    background: var(--v2-primary);
    color: #fff !important;
}

.cb-btn--primary i {
    color: #fff !important;
}

.cb-btn--primary:hover:not(:disabled) {
    background: var(--v2-primary-hover, #0e7490);
    box-shadow: var(--v2-shadow-sm);
}

.cb-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.cb-current-logo {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.cb-current-logo-label {
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-3);
}

.cb-current-logo-frame {
    width: 120px;
    height: 80px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-1);
    border: 1px solid var(--v2-border);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 8px;
}

.cb-current-logo-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    display: block;
}

@media (max-width: 900px) {
    .cb-v2 {
        padding: 16px 18px 32px;
    }

    .cb-hero-title {
        font-size: 1.375rem;
    }

    .cb-grid {
        grid-template-columns: 1fr;
    }

    .cb-form {
        padding: 16px;
    }

    .cb-card-header {
        padding: 14px 16px;
    }

    .cb-actions {
        flex-direction: column-reverse;
        align-items: stretch;
    }

    .cb-btn {
        justify-content: center;
    }
}
</style>
